import { useEffect, useState } from "react";
import { IndividualAirflowJob } from "../pages/IndividualJob";
import DetailsHeader from "./JobDetailsComponents/DetailsHeader";
import DetailsTabs from "./JobDetailsComponents/DetailsTabs";
import DetailsTabPanels from "./JobDetailsComponents/DetailsTabPanels";

export interface DetailsSelection {
    dag_id: string,
    run: string,
    task_id?: string,
    map_index?: number,
    details: JobDetailsType,
    metadata?: any, 
    mapped_tasks?: MappedTasksType[],
    logs?: any,
}

export type DetailsTabType = 'details' | 'metadata' | 'mapped_tasks' | 'logs'

export interface MappedTasksType {
    map_index: number,
    state: string,
    duration: number,
    start_date: string,
    end_date: string,
    try_number: number
}

export interface JobDetailsType {
    state: string,
    run_id: string,
    duration: number,
    start_date: string,
    end_date: string,
    run_type?: string,
    queued_at?: string,
    externally_triggered?: boolean,
    task_id?: string,
    map_index?: number,
    max_tries?: number,
    try_number?: number,
    mapped_task_instances? : number, 
}

export default function JobDetails({
    job,
    tasks,
    selectedRun,
}: {
    job: IndividualAirflowJob | null,
    tasks: any[],
    selectedRun: {
        dag_id: string,
        task_id?: string,
        map_index?: number
    },
}) {

    const [ selection, setSelection ] = useState<DetailsSelection | null>(null);

    const [ selectedTab, setSelectedTab ] = useState<DetailsTabType>('details');

    const handleTabChange = (event: React.SyntheticEvent, newValue: DetailsTabType) => {
        setSelectedTab(newValue);
      };

    useEffect(() => {
        if (tasks.length > 0 && job) {
            let selected: any = {};

            if (selectedRun.task_id && selectedRun.map_index !== undefined) {
                const task = tasks.find(t => t.task_instance.task_id === selectedRun.task_id && t.task_instance.map_index === selectedRun.map_index);
    
                if (task) {
                    const { dag_id, task_id, map_index, duration, start_date, end_date, max_tries, state, try_number } = task.task_instance;
                    const endDate = end_date ? new Date(end_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';
                    const startDate = start_date ? new Date(start_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';

                    let mapped_tasks: {
                        map_index: number,
                        state: string,
                        duration: number,
                        start_date: string,
                        end_date: string,
                        try_number: number
                    }[] = [];

                    tasks.forEach(t => {
                        if (t.task_instance.task_id === task_id) {
                            const taskStartDate = t.task_instance.start_date ?  new Date(t.task_instance.start_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';
                            const taskEndDate = t.task_instance.end_date ? new Date(t.task_instance.end_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';

                            let taskDuration = 0;

                            if (taskStartDate && taskEndDate) {
                                taskDuration = taskEndDate.getTime() - taskStartDate.getTime();
                            }

                            mapped_tasks.push({
                                map_index: t.task_instance.map_index,
                                state: t.task_instance.state,
                                duration: taskDuration,
                                start_date: taskStartDate ? taskStartDate.toLocaleString() : 'Not available',
                                end_date: taskEndDate ? taskEndDate.toLocaleString() : 'Not available',
                                try_number: t.task_instance.try_number,
                            });
                        }
                    });

                    selected = {
                        dag_id,
                        run: startDate ? startDate.toLocaleString() : 'Not available',
                        task_id,
                        map_index,
                        details: {
                            state,
                            task_id,
                            run_id: job.run_id,
                            map_index,
                            duration,
                            start_date: startDate ? startDate.toLocaleString() : 'Not available', 
                            end_date: endDate ? endDate.toLocaleString() : 'Not available',
                            max_tries,
                            try_number,
                            mapped_task_instances: mapped_tasks.length, 
                        },
                        mapped_tasks: mapped_tasks.length > 1 ? mapped_tasks : undefined,
                        logs: task.xcoms,
                    };
                }
            } else {
                const endDate = job.end_date ?  new Date(job.end_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';
                const startDate = job.start_date ? new Date(job.start_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';
                const queuedDate = job.queued_at ? new Date(job.queued_at.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z')) : '';

                let duration = 0;

                if (startDate && endDate) {
                    duration = endDate.getTime() - startDate.getTime();
                };
                 

                selected = {
                    dag_id: selectedRun.dag_id,
                    run: startDate ? startDate.toLocaleString() : 'Not available',
                    details: {
                        state: job.dag_final_state_report && job.dag_final_state_report.dag_state ? job.dag_final_state_report.dag_state : job.state,
                        run_id: job.run_id,
                        run_type: job.run_type,
                        duration,
                        queued_at: queuedDate ? queuedDate.toLocaleString() : 'Not available',
                        start_date: startDate ? startDate.toLocaleString() : 'Not available', 
                        end_date: endDate ? endDate.toLocaleString() : 'Not available',
                        externally_triggered: job.external_trigger,
                    }
                }
            };

            setSelection(selected);
            setSelectedTab('details');
        }
        
    }, [selectedRun, tasks, job]);

    return (
        <div className="w-full h-full max-h-[600px] grow flex flex-col justify-start items-stretch gap-6">
            {selection && (
                <>
                    <DetailsHeader 
                        header={{
                            dag_id: selection.dag_id,
                            run: selection.run,
                            task_id: selection.task_id,
                            map_index: selection.map_index,
                        }}
                    />

                    <DetailsTabs
                        selection={selection}
                        handleTabChange={handleTabChange}
                        tab={selectedTab}
                    />
                    <DetailsTabPanels 
                        selection={selection}
                        tab={selectedTab}
                    />
                </>
            )}
        </div>
    )
}